import { useCallback, useEffect, useRef, useState } from 'react'

/** How often a held arrow key or nav button steps to the next item. */
export const heldNavigationRepeatIntervalMs = 100
/** How long a nav button must be held before it starts repeating. */
export const heldNavigationButtonDelayMs = 350

export type NavigationDirection = -1 | 1

/**
 * Left/right arrow keys and press-and-hold nav buttons that keep stepping while held,
 * at most once per `heldNavigationRepeatIntervalMs`.
 *
 * `scrubbing` is true while a hold is repeating, so the viewer can avoid loading
 * every item it passes. It ends on release, when `onStep` finds nothing to move to, and
 * when the window loses focus or is hidden, since the release then happens somewhere
 * this page never hears about.
 */
export function useHeldNavigation({
  onStep,
  keyboardEnabled = true,
}: {
  /** Returns whether it moved; a hold stops once there is nowhere left to go. */
  onStep: (direction: NavigationDirection) => boolean
  keyboardEnabled?: boolean
}) {
  const [scrubbing, setScrubbing] = useState(false)
  // A hold keeps calling the latest onStep, which knows where the viewer is now.
  const onStepRef = useRef(onStep)
  useEffect(() => {
    onStepRef.current = onStep
  })
  const holdTimer = useRef(0)

  const stop = useCallback(() => {
    window.clearTimeout(holdTimer.current)
    window.clearInterval(holdTimer.current)
    holdTimer.current = 0
    window.removeEventListener('pointerup', stop)
    window.removeEventListener('pointercancel', stop)
    setScrubbing(false)
  }, [])

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        stop()
      }
    }
    window.addEventListener('blur', stop)
    document.addEventListener('visibilitychange', stopWhenHidden)
    return () => {
      window.removeEventListener('blur', stop)
      document.removeEventListener('visibilitychange', stopWhenHidden)
      stop()
    }
  }, [stop])

  useEffect(() => {
    if (!keyboardEnabled) {
      return
    }
    let lastStepAt = 0
    const isArrow = (e: KeyboardEvent) =>
      e.key === 'ArrowLeft' || e.key === 'ArrowRight'
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.shiftKey ||
        e.ctrlKey ||
        e.target instanceof HTMLTextAreaElement ||
        !isArrow(e)
      ) {
        return
      }
      const now = performance.now()
      if (e.repeat && now - lastStepAt < heldNavigationRepeatIntervalMs) {
        return
      }
      lastStepAt = now
      const moved = onStepRef.current(e.key === 'ArrowLeft' ? -1 : 1)
      // A held key at the first or last item stops scrubbing, as a held button does, so
      // that item loads without waiting for the key to be released.
      setScrubbing(e.repeat && moved)
    }
    const handleKeyUp = (e: KeyboardEvent) => {
      if (isArrow(e)) {
        setScrubbing(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      setScrubbing(false)
    }
  }, [keyboardEnabled])

  const startHold = useCallback(
    (direction: NavigationDirection) => {
      stop()
      if (!onStepRef.current(direction)) {
        return
      }
      // The release is heard on the window, not the button: the button unmounts when
      // the hold reaches the first or last item, and a hold keeps going when the pointer
      // slides off the button, for a mouse as for touch.
      window.addEventListener('pointerup', stop)
      window.addEventListener('pointercancel', stop)
      holdTimer.current = window.setTimeout(() => {
        setScrubbing(true)
        holdTimer.current = window.setInterval(() => {
          if (!onStepRef.current(direction)) {
            stop()
          }
        }, heldNavigationRepeatIntervalMs)
      }, heldNavigationButtonDelayMs)
    },
    [stop],
  )

  const buttonProps = useCallback(
    (direction: NavigationDirection) => ({
      onPointerDown: (e: React.PointerEvent) => {
        if (e.button === 0) {
          startHold(direction)
        }
      },
      onPointerUp: stop,
      onPointerCancel: stop,
      // Pointer presses step on pointerdown; only keyboard activation (detail 0)
      // arrives here as a click that still needs to step.
      onClick: (e: React.MouseEvent) => {
        if (e.detail === 0) {
          onStepRef.current(direction)
        }
      },
    }),
    [startHold, stop],
  )

  return { scrubbing, buttonProps }
}
